import React, { useMemo } from "react";
import { GenieCapabilityCategory } from "@/data/genie/categories";
import { useAgentPageByUrlName } from "@/hooks/agent/useAgentPage";
import { useVoiceAgentConversation } from "@/hooks/voice/useVoiceAgentConversation";
import CategoryActions from "./CategoryActions";
import TalkableGenieAvatar from "./TalkableGenieAvatar";

interface CategoryCardProps {
  category: GenieCapabilityCategory;
  onAction?: (category: GenieCapabilityCategory) => void;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ category, onAction }) => {
  const avatarSrc = useMemo(() => {
    const count = 13;
    const index = Math.floor(Math.random() * count) + 1;
    return `/genie-images/${index}.png`;
  }, []);

  const tags = useMemo(() => {
    const source =
      category.tags && category.tags.length > 0
        ? category.tags
        : category.description
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean);
    return source.slice(0, 8);
  }, [category]);

  const tagRows = useMemo(() => {
    const rows: string[][] = [];
    for (let i = 0; i < tags.length; i += 4) {
      rows.push(tags.slice(i, i + 4));
    }
    return rows;
  }, [tags]);

  // Optional: wire voice to control ring states; keeps center image and container sizes unchanged
  const { data: agentPage } = useAgentPageByUrlName(category.share?.slug);

  const voiceAgent = useVoiceAgentConversation(agentPage?.elevenlabs_id, {
    welcomeMessage: agentPage?.welcome_message || agentPage?.page?.welcome_message || "Click to start",
    agentData: agentPage || undefined,
  });

  const handleAvatarClick = async () => {
    if (!agentPage?.elevenlabs_id) return;
    await voiceAgent.toggleConversation();
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3">
      <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-4 h-full">
        <div className="min-w-0">
          <h3 className="text-lg md:text-xl font-semibold text-gray-900">{category.title}</h3>
          {/* Tags: 4 per row, left-justified with natural pill widths */}
          <div className="mt-1 space-y-1.5">
            {tagRows.map((row, rowIdx) => (
              <div key={rowIdx} className="flex flex-wrap gap-1.5">
                {row.map((tag, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-2 py-[2px] rounded-full text-xs md:text-sm text-gray-800 border"
                    style={{
                      borderColor: "#4E9CFF",
                      backgroundColor: "#ffffff",
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-start md:pl-2 w-full">
          <div className="flex items-center gap-4 w-full">
            <TalkableGenieAvatar
              avatarSrc={avatarSrc}
              onClick={handleAvatarClick}
              isConnecting={voiceAgent.isConnecting}
              listening={voiceAgent.listening}
              speaking={voiceAgent.speaking}
              connected={voiceAgent.connected}
              className="h-24 w-24 md:h-28 md:w-28"
            />
            {/* Separator */}
            <div className="hidden md:block w-px h-10 bg-gray-200 mx-2 md:mx-3" />

            <CategoryActions category={category} onLiveAgent={() => onAction?.(category)} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryCard;
